/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { cached } from '@glimmer/tracking';
import { action } from '@ember/object';
import Component from '@glimmer/component';

const SIBLINGS = 1;

interface IPaginationItem {
  page: number | null;
}

interface IArgs {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  // Keep the controls on a single page so the layout doesn't jump.
  alwaysVisible?: boolean;
}

interface PaginationSignature {
  Element: HTMLElement;

  Args: IArgs;
}

export default class UiPaginationComponent extends Component<PaginationSignature> {
  @cached
  get currentPage(): number {
    return Number(this.args.currentPage);
  }

  @cached
  get totalPages(): number {
    return Number(this.args.totalPages);
  }

  @cached
  get isVisible(): boolean {
    return this.totalPages > (this.args.alwaysVisible ? 0 : 1);
  }

  @cached
  get hasPrevious(): boolean {
    return this.currentPage > 1;
  }

  @cached
  get hasNext(): boolean {
    return this.currentPage < this.totalPages;
  }

  @cached
  get items(): IPaginationItem[] {
    const { currentPage, totalPages } = this;

    const pages = new Set([1, totalPages]);

    for (
      let page = currentPage - SIBLINGS;
      page <= currentPage + SIBLINGS;
      page++
    ) {
      if (page >= 1 && page <= totalPages) {
        pages.add(page);
      }
    }

    const sorted = [...pages].sort((a, b) => a - b);
    const items: IPaginationItem[] = [];

    let previous = 0;

    for (const page of sorted) {
      if (page - previous === 2) {
        items.push({ page: previous + 1 });
      } else if (page - previous > 2) {
        items.push({ page: null });
      }

      items.push({ page });

      previous = page;
    }

    return items;
  }

  @action
  handlePageClick(page: number | null): void {
    if (!page || page === this.currentPage) {
      return;
    }

    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.args.onPageChange(page);
  }

  @action
  handlePrevious(): void {
    this.handlePageClick(this.currentPage - 1);
  }

  @action
  handleNext(): void {
    this.handlePageClick(this.currentPage + 1);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Pagination': typeof UiPaginationComponent;
  }
}
